import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface Event {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startAt: string;
  endAt: string | null;
  location: string | null;
  capacity: number | null;
  audienceType: string;
  audienceIds: string[] | null;
  createdById: string;
  createdAt: string;
}

interface Pack { id: string; name: string; }
interface User { id: string; displayName: string; email: string; role: string; }

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Combine a date string (YYYY-MM-DD) and time string (HH:MM) into an ISO string */
function combineDateTime(date: string, time: string): string {
  return `${date}T${time || '00:00'}`;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#b8860b',
  approved: '#2e7d32',
  rejected: '#b71c1c',
  cancelled: '#555',
  completed: '#1565c0',
};

const AUDIENCE_LABELS: Record<string, string> = {
  club: 'Club-wide',
  packs: 'Specific Packs',
  members: 'Specific Members',
};

const AUDIENCE_COLORS: Record<string, string> = {
  club: '#1a4a3a',
  packs: '#2a3a5a',
  members: '#3a2a5a',
};

export function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    capacity: '',
    audience_type: 'club' as 'club' | 'packs' | 'members',
    audience_ids: [] as string[],
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Audience options
  const [packs, setPacks] = useState<Pack[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [audienceLoading, setAudienceLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const json = await apiFetch<Event[]>('/api/admin/events');
      if (!json.success) { setError(json.error ?? 'Failed to load events'); return; }
      setEvents(json.data ?? []);
    } catch {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load packs or users when audience type changes
  useEffect(() => {
    if (form.audience_type === 'club') return;
    if (form.audience_type === 'packs' && packs.length > 0) return;
    if (form.audience_type === 'members' && users.length > 0) return;

    setAudienceLoading(true);
    const endpoint = form.audience_type === 'packs' ? '/api/admin/packs' : '/api/admin/users';
    apiFetch<Pack[] | User[]>(endpoint)
      .then((json) => {
        if (!json.success) return;
        if (form.audience_type === 'packs') setPacks((json.data ?? []) as Pack[]);
        else setUsers((json.data ?? []) as User[]);
      })
      .finally(() => setAudienceLoading(false));
  }, [form.audience_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAudienceId = (id: string) => {
    setForm((f) => ({
      ...f,
      audience_ids: f.audience_ids.includes(id)
        ? f.audience_ids.filter((x) => x !== id)
        : [...f.audience_ids, id],
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date) { setCreateError('Name and start date are required'); return; }
    if (form.audience_type !== 'club' && form.audience_ids.length === 0) {
      setCreateError(`Select at least one ${form.audience_type === 'packs' ? 'pack' : 'member'}`);
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const json = await apiFetch('/api/admin/events', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          start_at: combineDateTime(form.start_date, form.start_time),
          end_at: form.end_date ? combineDateTime(form.end_date, form.end_time) : undefined,
          location: form.location.trim() || undefined,
          capacity: form.capacity ? parseInt(form.capacity) : undefined,
          audience_type: form.audience_type,
          audience_ids: form.audience_type !== 'club' ? form.audience_ids : undefined,
        }),
      });
      if (!json.success) { setCreateError(json.error ?? 'Failed to create event'); return; }
      setForm({
        name: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '',
        location: '', capacity: '', audience_type: 'club', audience_ids: [],
      });
      setShowCreate(false);
      await loadEvents();
    } catch {
      setCreateError('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (event: Event, newStatus: string) => {
    try {
      await apiFetch(`/api/admin/events/${event.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      await loadEvents();
    } catch { /* noop */ }
  };

  const handleDelete = async (event: Event) => {
    if (!confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/events/${event.id}`, { method: 'DELETE' });
      await loadEvents();
    } catch { /* noop */ }
  };

  // Audience options list for the create form
  const audienceOptions: Array<{ id: string; label: string }> =
    form.audience_type === 'packs'
      ? packs.map((p) => ({ id: p.id, label: p.name }))
      : users
          .filter((u) => u.role === 'steward' || u.role === 'member' || u.role === 'owner' || u.role === 'events_admin')
          .map((u) => ({ id: u.id, label: `${u.displayName} (${u.role})` }));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Events</h1>
          <p style={styles.subtitle}>Create and manage club events.</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : '+ New Event'}
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {showCreate && (
        <form onSubmit={handleCreate} style={styles.createForm}>
          <h3 style={{ color: COLORS.textPrimary, margin: '0 0 16px' }}>Create Event</h3>

          {/* Row 1: Name + Location */}
          <div style={styles.formGrid}>
            <div style={styles.formField}>
              <label style={styles.label}>Name *</label>
              <input style={styles.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Event name" required />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Location</label>
              <input style={styles.input} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Main Lodge" />
            </div>
          </div>

          {/* Row 2: Start date + time */}
          <div style={{ ...styles.formGrid, gridTemplateColumns: '1fr 1fr 1fr 1fr', marginBottom: 12 }}>
            <div style={styles.formField}>
              <label style={styles.label}>Start Date *</label>
              <input style={styles.input} type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Start Time</label>
              <input style={styles.input} type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>End Date</label>
              <input style={styles.input} type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>End Time</label>
              <input style={styles.input} type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} disabled={!form.end_date} />
            </div>
          </div>

          {/* Row 3: Capacity + Audience type */}
          <div style={styles.formGrid}>
            <div style={styles.formField}>
              <label style={styles.label}>Capacity</label>
              <input style={styles.input} type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="Optional" />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Open To</label>
              <select
                style={styles.input}
                value={form.audience_type}
                onChange={(e) => setForm({ ...form, audience_type: e.target.value as 'club' | 'packs' | 'members', audience_ids: [] })}
              >
                <option value="club">Entire Club</option>
                <option value="packs">Specific Packs</option>
                <option value="members">Specific Members / Stewards</option>
              </select>
            </div>
          </div>

          {/* Audience picker */}
          {form.audience_type !== 'club' && (
            <div style={styles.formField}>
              <label style={styles.label}>
                {form.audience_type === 'packs' ? 'Select Packs' : 'Select Members'}
              </label>
              {audienceLoading ? (
                <div style={{ color: COLORS.textMuted, fontSize: 13, padding: 8 }}>Loading…</div>
              ) : (
                <div style={styles.checkboxList}>
                  {audienceOptions.map((opt) => (
                    <label key={opt.id} style={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={form.audience_ids.includes(opt.id)}
                        onChange={() => toggleAudienceId(opt.id)}
                        style={{ accentColor: COLORS.primaryGreen, marginRight: 8 }}
                      />
                      <span style={{ color: COLORS.textPrimary, fontSize: 13 }}>{opt.label}</span>
                    </label>
                  ))}
                  {audienceOptions.length === 0 && (
                    <div style={{ color: COLORS.textMuted, fontSize: 13, padding: 8 }}>None found.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div style={{ ...styles.formField, marginTop: 12 }}>
            <label style={styles.label}>Description</label>
            <textarea style={{ ...styles.input, height: 80, resize: 'vertical' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
          </div>

          {createError && <div style={styles.errorText}>{createError}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="submit" style={styles.btnPrimary} disabled={creating}>
              {creating ? 'Creating…' : 'Create Event'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={styles.muted}>Loading events…</div>
      ) : events.length === 0 ? (
        <div style={styles.muted}>No events yet.</div>
      ) : (
        <div style={styles.grid}>
          {events.map((event) => (
            <div key={event.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.cardTitle}>{event.name}</h3>
                  <div style={styles.cardMeta}>
                    {formatDate(event.startAt)}
                    {event.endAt && ` → ${formatDate(event.endAt)}`}
                    {event.location && ` · ${event.location}`}
                    {event.capacity && ` · Cap: ${event.capacity}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-start' }}>
                  <span style={{ ...styles.badge, backgroundColor: AUDIENCE_COLORS[event.audienceType] ?? '#333' }}>
                    {AUDIENCE_LABELS[event.audienceType] ?? event.audienceType}
                    {event.audienceIds && event.audienceIds.length > 0 && ` (${event.audienceIds.length})`}
                  </span>
                  <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[event.status] ?? '#555' }}>
                    {event.status}
                  </span>
                </div>
              </div>
              {event.description && <p style={styles.cardDesc}>{event.description}</p>}
              <div style={styles.cardActions}>
                <select
                  style={styles.statusSelect}
                  value={event.status}
                  onChange={(e) => handleStatusChange(event, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
                <button style={styles.btnDanger} onClick={() => handleDelete(event)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: COLORS.textPrimary, fontSize: 28, fontWeight: 700, margin: '0 0 4px' },
  subtitle: { color: COLORS.textMuted, fontSize: 14, margin: 0 },
  errorBanner: { backgroundColor: '#5c1a1a', color: '#ff8080', border: '1px solid #8b1a1a', borderRadius: 8, padding: '10px 16px', marginBottom: 20 },
  errorText: { color: '#ff8080', fontSize: 13, marginTop: 8 },
  muted: { color: COLORS.textMuted, fontSize: 14, padding: 16 },
  createForm: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20, marginBottom: 24 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  formField: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { color: COLORS.textMuted, fontSize: 12, fontWeight: 600 },
  input: { backgroundColor: COLORS.backgroundSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '9px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' as const, colorScheme: 'dark' as const },
  checkboxList: { backgroundColor: COLORS.backgroundSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '8px 4px', maxHeight: 180, overflowY: 'auto' as const, display: 'flex', flexDirection: 'column', gap: 2 },
  checkboxRow: { display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' },
  grid: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 },
  cardTitle: { color: COLORS.textPrimary, fontSize: 16, fontWeight: 700, margin: '0 0 4px' },
  cardMeta: { color: COLORS.textMuted, fontSize: 12 },
  cardDesc: { color: COLORS.textSecondary, fontSize: 14, margin: '0 0 12px', lineHeight: 1.5 },
  cardActions: { display: 'flex', gap: 8, alignItems: 'center' },
  badge: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, color: '#fff', borderRadius: 4, padding: '3px 8px' },
  statusSelect: { backgroundColor: COLORS.backgroundSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '6px 10px', fontSize: 13, cursor: 'pointer' },
  btnPrimary: { backgroundColor: COLORS.primaryGreen, color: COLORS.brassLight, border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDanger: { backgroundColor: '#5c1a1a', color: '#ff8080', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
};
